import { api, type Me } from "./api";

type NativeHandler = { postMessage(body: unknown): Promise<unknown> };
const handler = () => (window as unknown as { webkit?: { messageHandlers?: { ned?: NativeHandler } } }).webkit?.messageHandlers?.ned;
export const isNativeApp = () => !!handler();
export async function nativeCall<T = unknown>(method: string, args: object = {}): Promise<T> {
  const bridge = handler();
  if (!bridge) throw new Error("This feature requires the iPhone app.");
  return await bridge.postMessage({ method, ...args }) as T;
}

export type PushState = { token: string; env: "dev" | "prod"; authorized: boolean; denied?: boolean; error: string; inboxId: string };
// Serialize registration and logout so a late registration cannot reattach a signed-out device.
let operations: Promise<unknown> = Promise.resolve();
let registered = "";
function enqueue<T>(operation: () => Promise<T>): Promise<T> {
  const next = operations.then(operation, operation);
  operations = next.catch(() => {});
  return next;
}

export function syncNativeSession(me: Me | null) {
  if (!isNativeApp()) return Promise.resolve();
  return enqueue(async () => {
    if (!me) {
      registered = "";
      await nativeCall("tabs", { tabs: [], selected: "" });
      await nativeCall("badge", { count: 0 });
      return;
    }
    const state = await nativeCall<PushState>("pushState");
    const key = `${me.id}:${state.token}:${state.env}`;
    let registrationError: unknown;
    if (state.authorized && state.token && key !== registered) {
      try {
        await api("POST", "devices", { token: state.token, env: state.env });
        registered = key;
      } catch (error) { registrationError = error; }
    }
    let unread = me.unread;
    if (state.inboxId) {
      const result = await api<{ unread: number }>("POST", "inbox/read", { ids: [state.inboxId] });
      unread = result.unread;
      await nativeCall("notificationRead", { id: state.inboxId });
    }
    await nativeCall("badge", { count: unread });
    if (registrationError) console.warn("Push registration will retry:", (registrationError as Error).message);
    return unread;
  });
}

export function signOut() {
  return enqueue(async () => {
    if (isNativeApp()) {
      const { token } = await nativeCall<PushState>("pushState");
      if (token) await api("DELETE", "devices", { token });
    }
    await api("POST", "logout");
    registered = "";
  });
}

export async function enableNotifications(me: Me): Promise<string> {
  const { authorized } = await nativeCall<{ authorized: boolean }>("requestNotifications");
  if (!authorized) return "Notifications are off. You can enable them in iPhone Settings.";
  await syncNativeSession(me);
  const state = await nativeCall<PushState>("pushState");
  if (state.error) throw new Error(state.error);
  return state.token && registered === `${me.id}:${state.token}:${state.env}`
    ? "Notifications enabled."
    : "Permission granted. Connecting to push notifications… Reopen the app to retry if this continues.";
}
